/**
 * Reference-house helpers aligned with takeoffapp translator/refHouse.
 * HDD priority matches getRefHouseHdd / resolveRefHouseHddBin:
 * 1. program.options.refHouse.userHDD
 * 2. location.weather.location.value
 * 3. location.weather.heatingDegreeDay
 * Missing HDD uses the builder fallback of 3500, which is the SB-12 lt5000 bin.
 */

export const REF_HOUSE_CASES = [
    { value: "ersNbc2020", label: "ERS NBC 2020" },
    { value: "ersNbc2015", label: "ERS NBC 2015" },
    { value: "ontarioSb12", label: "Ontario SB-12" },
    { value: "nbc2020", label: "NBC 2020" },
    { value: "nbc2015", label: "NBC 2015" },
    { value: "nbc2025", label: "NBC 2025" },
    { value: "necb", label: "NECB" },
];

const HIDDEN_REF_HOUSE_CASES = new Set(["ersNbc2020", "ersNbc2015"]);

/** Cases offered in Settings and the upgrades-table package dialog. ERS cases stay hidden. */
export const SELECTABLE_REF_HOUSE_CASES = REF_HOUSE_CASES.filter(
    ({ value }) => !HIDDEN_REF_HOUSE_CASES.has(value)
).map((option) =>
    option.value === "necb"
        ? {
              ...option,
              label: "NECB (Coming soon)",
              disabled: true,
              disabledTooltip: "NECB reference houses are not available yet.",
          }
        : option
);

export const isSelectableRefHouseCase = (refCase = "") =>
    SELECTABLE_REF_HOUSE_CASES.some(({ value, disabled }) => value === refCase && !disabled);

const SB12_LT5000 = ["A1", "A2", "A3", "A4", "A5", "A6", "B1", "B2", "B3", "B4", "B5", "B6", "C1", "C2", "C3", "C4"];
const SB12_GTE5000 = ["A1", "A2", "A3", "A4", "A5", "A6", "B1", "B2", "B3", "B4", "B5", "B6", "C1", "C2"];

export const isReferenceHousePackage = (upgradePackage = {}) => upgradePackage?.packageType === "referenceHouse";

export const getRefHouseCaseLabel = (refCase = "") =>
    REF_HOUSE_CASES.find(({ value }) => value === refCase)?.label || refCase || "Reference house";

export const getRefHouseHdd = (modelData = {}) => {
    const userHdd = Number(modelData?.program?.options?.refHouse?.userHDD);
    if (userHdd > 0) {
        return userHdd;
    }

    const locationValue = Number(modelData?.location?.weather?.location?.value);
    if (locationValue > 0) {
        return locationValue;
    }

    const heatingDegreeDay = Number(modelData?.location?.weather?.heatingDegreeDay);
    if (heatingDegreeDay > 0) {
        return heatingDegreeDay;
    }

    return 0;
};

export const getSb12HddBin = (modelData = {}) => {
    const hdd = getRefHouseHdd(modelData);
    const value = hdd > 0 ? hdd : 3500;
    return value < 5000 ? "lt5000" : "gte5000";
};

export const getSb12PackageOptions = (modelData = {}) => {
    const ids = getSb12HddBin(modelData) === "gte5000" ? SB12_GTE5000 : SB12_LT5000;
    return ids.map((id) => ({ value: id, label: id }));
};

export const getReferenceHouseSummary = (upgradePackage = {}) => {
    const { refCase = "", packageId = "" } = upgradePackage.referenceHouse || {};
    const caseLabel = getRefHouseCaseLabel(refCase);
    if (refCase === "ontarioSb12" && packageId) {
        return `${caseLabel} ${packageId}`;
    }
    return caseLabel;
};

const findKey = (resultsData = {}, predicate) => Object.keys(resultsData || {}).find(predicate) || "";

export const findBaseCaseResultKey = (resultsData = {}, operatingConditions = "general") => {
    if (operatingConditions === "ersReference") {
        return findKey(
            resultsData,
            (key) => key === "ersReference" || (key.includes("onrh") && key.includes("BaseCase"))
        );
    }

    return findKey(
        resultsData,
        (key) => key.includes("BaseCase") && key.includes(operatingConditions)
    );
};

/**
 * Column result key for the upgrades table and Review.
 * Reference-house columns always read a base-case key. Normal packages keep AllUpgrades,
 * with the existing BaseCase fallback for pseudo-upgrades.
 */
/**
 * Case key for a stored result column.
 * Reference-house and base columns read *BaseCase. Normal packages read *AllUpgrades,
 * falling back to *BaseCase for pseudo-upgrades. Native soc* keys win when they exist.
 */
export const resolveStoredCaseKey = ({
    resultsData = {},
    packageId = "base",
    packageType = "",
    resultsRole = "",
    operatingConditions,
} = {}) => {
    const keys = Object.keys(resultsData || {});
    const defaultOpCond = keys.some((key) => key.includes("soc")) ? "soc" : "general";
    const opCond = defaultOpCond === "general" ? "general" : operatingConditions || defaultOpCond;
    const useBaseCase =
        !packageId ||
        packageId === "base" ||
        packageType === "referenceHouse" ||
        resultsRole === "referenceHouse" ||
        resultsRole === "base";

    return (
        resolveColumnResultKey({
            resultsData,
            operatingConditions: opCond,
            useBaseCase,
        }) || (useBaseCase ? "generalBaseCase" : "generalAllUpgrades")
    );
};

export const getSocApproximationLabel = (effectiveOperatingConditions = "") =>
    effectiveOperatingConditions === "socApproximation" ? "General mode — SOC approximated" : "";

export const resolveColumnResultKey = ({
    resultsData = {},
    operatingConditions = "general",
    useBaseCase = false,
}) => {
    if (useBaseCase || operatingConditions === "ersReference") {
        return findBaseCaseResultKey(resultsData, operatingConditions);
    }

    return (
        findKey(resultsData, (key) => key.includes("AllUpgrades") && key.includes(operatingConditions)) ||
        findBaseCaseResultKey(resultsData, operatingConditions)
    );
};
